/**
 * CustomMapStore — persistent storage for user-created maps.
 *
 * Maps are saved as JSON in localStorage under a single key.
 * Provides CRUD operations, file-based export/import, and
 * syncs with the runtime ALL_MAPS array on load.
 *
 * Events emitted via EventBus:
 *   - GameEvents.CUSTOM_MAPS_CHANGED → fired after any save/delete
 */
import { EventBus, GameEvents } from '@/events/EventBus';
import { MapDefinition } from '@/map/MapDefs';
import { ALL_MAPS } from '@/map/MapData';

const STORAGE_KEY = 'robogame_custom_maps';

// Number of built-in official maps (indices 0..OFFICIAL_COUNT-1 in ALL_MAPS)
const OFFICIAL_COUNT = 11;

class CustomMapStoreClass {
  private maps: MapDefinition[] = [];
  private loaded = false;

  // ── Core CRUD ──

  /** Load custom maps from localStorage and push into ALL_MAPS (call once at boot). */
  load(): void {
    if (this.loaded) return;
    this.loaded = true;

    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const parsed: MapDefinition[] = JSON.parse(raw);
      if (!Array.isArray(parsed)) return;
      this.maps = parsed.filter(m => m && m.id && m.grid && m.cols && m.rows);
    } catch (e) {
      console.warn('[CustomMapStore] load failed:', e);
      this.maps = [];
    }

    // Sync into ALL_MAPS: remove stale custom entries, then re-add from store
    this.syncToAllMaps();
  }

  /** Get all custom maps (read-only snapshot). */
  getAll(): ReadonlyArray<MapDefinition> {
    return this.maps;
  }

  /** Get a custom map by id, or undefined. */
  getById(id: string): MapDefinition | undefined {
    return this.maps.find(m => m.id === id);
  }

  /** Save (insert or update) a custom map. */
  save(map: MapDefinition): void {
    const idx = this.maps.findIndex(m => m.id === map.id);
    // Deep-clone to avoid shared references
    const clone = JSON.parse(JSON.stringify(map)) as MapDefinition;
    if (idx >= 0) {
      this.maps[idx] = clone;
    } else {
      this.maps.push(clone);
    }
    this.persist();
    this.syncToAllMaps();
    EventBus.emit(GameEvents.CUSTOM_MAPS_CHANGED);
  }

  /** Delete a custom map by id. */
  delete(id: string): boolean {
    const idx = this.maps.findIndex(m => m.id === id);
    if (idx < 0) return false;
    this.maps.splice(idx, 1);
    this.persist();
    this.syncToAllMaps();
    EventBus.emit(GameEvents.CUSTOM_MAPS_CHANGED);
    return true;
  }

  /** Total count of stored custom maps. */
  get count(): number {
    return this.maps.length;
  }

  // ── File export / import ──

  /** Download a single map as a .json file. */
  exportToFile(map: MapDefinition): void {
    const json = JSON.stringify(map, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${map.name.replace(/[^a-zA-Z0-9_-]/g, '_')}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  /** Open a file picker, read a .json map, and return the parsed MapDefinition (or null). */
  importFromFile(): Promise<MapDefinition | null> {
    return new Promise(resolve => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.json,application/json';
      input.onchange = () => {
        const file = input.files?.[0];
        if (!file) { resolve(null); return; }
        const reader = new FileReader();
        reader.onload = () => {
          try {
            const data = JSON.parse(reader.result as string);
            if (!data.grid || !data.cols || !data.rows) {
              resolve(null);
              return;
            }
            // Ensure it has a unique id
            if (!data.id) data.id = `imported_${Date.now()}`;
            resolve(data as MapDefinition);
          } catch (e) {
            console.warn('[CustomMapStore] importFromFile parse failed:', e);
            resolve(null);
          }
        };
        reader.onerror = () => resolve(null);
        reader.readAsText(file);
      };
      // If user cancels the picker, resolve null
      input.oncancel = () => resolve(null);
      input.click();
    });
  }

  /** Open a file picker, read a .json that contains an array of maps, save them all. Returns count imported. */
  importBatchFromFile(): Promise<number> {
    return new Promise(resolve => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.json,application/json';
      input.onchange = () => {
        const file = input.files?.[0];
        if (!file) { resolve(0); return; }
        const reader = new FileReader();
        reader.onload = () => {
          try {
            const data = JSON.parse(reader.result as string);
            const arr: MapDefinition[] = Array.isArray(data) ? data : [data];
            let count = 0;
            for (const map of arr) {
              if (!map.grid || !map.cols || !map.rows) continue;
              if (!map.id) map.id = `imported_${Date.now()}_${count}`;
              this.save(map);
              count++;
            }
            resolve(count);
          } catch (e) {
            console.warn('[CustomMapStore] importBatchFromFile parse failed:', e);
            resolve(0);
          }
        };
        reader.onerror = () => resolve(0);
        reader.readAsText(file);
      };
      input.oncancel = () => resolve(0);
      input.click();
    });
  }

  // ── Internal ──

  private persist(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.maps));
    } catch (e) { console.warn('[CustomMapStore] persist failed:', e); }
  }

  /** Keep ALL_MAPS in sync: official maps (0..8) + custom maps from store. */
  private syncToAllMaps(): void {
    // Trim ALL_MAPS back to official-only
    ALL_MAPS.length = OFFICIAL_COUNT;
    // Append all stored custom maps
    for (const m of this.maps) {
      ALL_MAPS.push(m);
    }
  }
}

export const CustomMapStore = new CustomMapStoreClass();
